"use strict";
(() => {
  let busy = false,
    returnFocus = null;
  const lockedControls = new Map();
  const find = (id) => document.getElementById(id);
  const cartDialog = () => find("CartDrawer");
  const root = () => cartDialog()?.dataset.rootUrl || "/";
  const route = (path) => root().replace(/\/?$/, "/") + path;
  function announce(message) {
    const status = find("CartStatus");
    if (status) status.textContent = message;
  }
  function showError(message) {
    const error = find("CartError");
    if (error) {
      error.querySelector("[data-cart-error-message]").textContent = message;
      error.hidden = false;
    }
    announce(message);
  }
  function clearError() {
    const error = find("CartError");
    if (error) {
      error.querySelector("[data-cart-error-message]").textContent = "";
      error.hidden = true;
    }
  }
  let menuToggleHome = null;
  function prepareMenuToggle(dialog) {
    if (dialog.id !== "MenuDrawer") return;
    const button = document.querySelector('.mobile-menu[data-open-dialog="MenuDrawer"]');
    if (!button || menuToggleHome) return;
    const rect = button.getBoundingClientRect();
    const styles = getComputedStyle(button);
    const placeholder = document.createElement("span");
    placeholder.style.cssText = `display:inline-flex;flex-shrink:0;width:${rect.width}px;height:${rect.height}px;margin:${styles.margin}`;
    button.before(placeholder);
    menuToggleHome = { button, placeholder };
    dialog.style.setProperty("--menu-toggle-x", `${rect.left}px`);
    dialog.style.setProperty("--menu-toggle-y", `${rect.top}px`);
    dialog.style.setProperty("--menu-toggle-size", `${rect.height}px`);
    button.classList.add("menu-toggle-floating");
    dialog.prepend(button);
    // Paint the original lines before rotating them into the close icon.
    requestAnimationFrame(() => {
      if (!dialog.open || dialog.classList.contains("is-closing")) return;
      button.setAttribute("aria-expanded", "true");
      button.setAttribute("aria-label", "Close menu");
    });
  }
  function restoreMenuToggle(dialog) {
    if (dialog.id !== "MenuDrawer" || !menuToggleHome) return;
    const { button, placeholder } = menuToggleHome;
    placeholder.replaceWith(button);
    button.classList.remove("menu-toggle-floating");
    button.setAttribute("aria-expanded", "false");
    button.setAttribute("aria-label", "Open menu");
    menuToggleHome = null;
  }
  let menuViewportWidth = window.innerWidth;
  window.addEventListener("resize", () => {
    if (window.innerWidth === menuViewportWidth) return;
    menuViewportWidth = window.innerWidth;
    const menu = find("MenuDrawer");
    if (menu?.open) menu.close();
  });
  function offerCornerTransform(card, launcher) {
    if (!launcher || launcher.hidden || !card.offsetWidth || !card.offsetHeight) return null;
    const corner = launcher.getBoundingClientRect();
    if (!corner.width || !corner.height) return null;
    const rect = card.getBoundingClientRect();
    const transform = getComputedStyle(card).transform;
    const matrix = new DOMMatrixReadOnly(transform === "none" ? undefined : transform);
    // Remove the current transform without rounding fractional layout dimensions.
    const x = corner.left + corner.width / 2 - (rect.left + rect.width / 2 - matrix.e);
    const y = corner.top + corner.height / 2 - (rect.top + rect.height / 2 - matrix.f);
    const scaleX = corner.width / (rect.width / matrix.a), scaleY = corner.height / (rect.height / matrix.d);
    return { transform: `translate3d(${x}px, ${y}px, 0) scale(${scaleX}, ${scaleY})`, scaleX, scaleY };
  }
  const offerMorphs = new WeakMap();
  function clearOfferMorph(dialog) {
    const motion = offerMorphs.get(dialog);
    offerMorphs.delete(dialog);
    dialog.classList.remove("is-offer-morphing");
    motion?.animations.forEach(animation => animation.cancel());
  }
  function playOfferMorph(motion, closing, time = motion.primary.currentTime) {
    motion.closing = closing;
    const rate = closing ? 1 : -1;
    const clock = document.timeline.currentTime;
    // All parts use the same clock and progress, including an interrupted reversal.
    motion.animations.forEach(animation => {
      animation.pause();
      animation.currentTime = time;
      animation.playbackRate = rate;
      animation.play();
      if (clock !== null) animation.startTime = clock - time / rate;
    });
  }
  function morphOffer(dialog, launcher, opening = false) {
    const card = dialog.querySelector("[data-offer-card]");
    const interior = dialog.querySelector("[data-offer-interior]");
    const label = dialog.querySelector(".sign-in-offer-morph-label");
    const backdrop = dialog.querySelector(".sign-in-offer-backdrop");
    if (!card?.animate || !interior || !label || !backdrop || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return false;
    const active = offerMorphs.get(dialog);
    if (active && !opening) {
      dialog.classList.add("is-closing", "is-minimizing");
      playOfferMorph(active, true);
      return true;
    }
    const corner = offerCornerTransform(card, launcher);
    if (!corner) return false;
    if (opening) dialog.classList.add("offer-morph-entered");
    const current = getComputedStyle(card);
    const matrix = new DOMMatrixReadOnly(current.transform === "none" ? undefined : current.transform);
    const expanded = { transform: current.transform, opacity: current.opacity, backgroundColor: current.backgroundColor,
      borderColor: current.borderColor, borderRadius: current.borderRadius, boxShadow: current.boxShadow, clipPath: "none", offset: 0 };
    const shade = getComputedStyle(backdrop).opacity;
    const options = { duration: 620, easing: "cubic-bezier(.32, 0, .18, 1)", fill: "both" };
    const contents = [], labels = [];
    for (let step = 0; step <= 40; step += 1) {
      const p = step / 40;
      const sx = matrix.a + (corner.scaleX - matrix.a) * p;
      const sy = matrix.d + (corner.scaleY - matrix.d) * p;
      // Keep the photograph, text and corner lettering in proportion as the surface folds.
      contents.push({ offset: p, transform: `scale(${matrix.a / sx}, ${matrix.d / sy})`, opacity: Math.max(0, 1 - p * 2) });
      labels.push({ offset: p, transform: `scale(${1 / sx}, ${1 / sy})`, opacity: Math.max(0, (p - .5) * 2) });
    }
    dialog.classList.add("is-offer-morphing");
    if (!opening) dialog.classList.add("is-closing", "is-minimizing");
    const primary = card.animate([
      expanded,
      { offset: .25, clipPath: "none", boxShadow: "0 0 0 0 #0000" },
      { offset: .251, clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" },
      { offset: 1, transform: corner.transform, opacity: 1, backgroundColor: "#fff", borderColor: "transparent",
        borderRadius: "0px", boxShadow: "0 0 0 0 #0000", clipPath: "polygon(0 0, 100% 100%, 100% 100%, 0 100%)" },
    ], options);
    const motion = { primary, closing: !opening, animations: [primary, interior.animate(contents, options), label.animate(labels, options),
      backdrop.animate([{ opacity: opening ? 1 : shade }, { opacity: 0 }], options)] };
    offerMorphs.set(dialog, motion);
    playOfferMorph(motion, !opening, opening ? options.duration : 0);
    primary.finished.then(() => {
      if (offerMorphs.get(dialog) !== motion) return;
      if (motion.closing) {
        if (dialog.open) dialog.close();
        // Reveal the real tab in the same frame that the matching surface leaves the modal layer.
        if (!document.querySelector("dialog[open]")) document.documentElement.classList.remove("dialog-open");
      } else clearOfferMorph(dialog);
    }).catch(() => {});
    return true;
  }
  function openDialog(dialog, fromOfferTab = false) {
    if (!dialog || dialog.open) return;
    const previous = document.querySelector("dialog[open]");
    const nextReturnFocus = previous?.contains(document.activeElement) ? returnFocus : document.activeElement;
    document.querySelectorAll("dialog[open]").forEach((d) => d.close());
    returnFocus = nextReturnFocus;
    prepareMenuToggle(dialog);
    dialog.showModal();
    const offerScroll = dialog.querySelector(".sign-in-offer-scroll");
    if (offerScroll) offerScroll.scrollTop = 0;
    if (fromOfferTab && dialog.hasAttribute("data-welcome-offer")) {
      morphOffer(dialog, document.querySelector("[data-offer-launcher]"), true);
    }
    document.documentElement.classList.add("dialog-open");
  }
  const dialogCloseAnimations = new WeakMap();
  function closeDialog(dialog) {
    if (!dialog?.open || dialog.classList.contains("is-closing")) return;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const isOffer = dialog.hasAttribute("data-welcome-offer");
    const card = dialog.querySelector("[data-offer-card]") || dialog;
    const launcher = isOffer && dialog.dataset.offerSignedIn !== "true" ? document.querySelector("[data-offer-launcher]") : null;
    if (launcher && !launcher.hidden) returnFocus = launcher;
    if (isOffer && launcher && !launcher.hidden && morphOffer(dialog, launcher)) return;
    if (isOffer && card.animate && !reducedMotion) {
      const current = getComputedStyle(card);
      const from = { opacity: current.opacity, transform: current.transform };
      // Start dismissal from the visible frame, even during the entrance.
      dialog.style.setProperty("--offer-backdrop-from", getComputedStyle(dialog.querySelector(".sign-in-offer-backdrop")).opacity);
      dialog.classList.add("is-closing");
      const animation = card.animate(
        [from, { opacity: 0, transform: "translate3d(0, 8px, 0) scale(.985)" }],
        { duration: 240, easing: "cubic-bezier(.4, 0, .6, 1)", fill: "forwards" },
      );
      dialogCloseAnimations.set(dialog, animation);
      animation.finished.then(() => { if (dialog.open) dialog.close(); }).catch(() => {});
      return;
    }
    const sheet = dialog.querySelector(".menu-sheet-content");
    if (dialog.id !== "MenuDrawer" || !sheet?.animate || reducedMotion) {
      dialog.close();
      return;
    }
    const currentTransform = getComputedStyle(sheet).transform;
    sheet.style.animation = "none";
    dialog.classList.add("is-closing");
    const toggle = dialog.querySelector(".mobile-menu");
    toggle?.setAttribute("aria-expanded", "false");
    const animation = sheet.animate(
      [{ transform: currentTransform }, { transform: "translate3d(-100%, 0, 0)" }],
      { duration: 260, easing: "cubic-bezier(.4, 0, .8, .2)", fill: "forwards" },
    );
    dialogCloseAnimations.set(dialog, animation);
    animation.finished.then(() => { if (dialog.open) dialog.close(); }).catch(() => {});
  }
  function bindDialog(dialog) {
    if (dialog.dataset.bound) return;
    dialog.dataset.bound = "true";
    dialog.addEventListener("click", (e) => {
      if (e.target !== dialog) return;
      const r = (dialog.querySelector("[data-offer-card]") || dialog).getBoundingClientRect();
      if (
        e.clientX < r.left ||
        e.clientX > r.right ||
        e.clientY < r.top ||
        e.clientY > r.bottom
      )
        closeDialog(dialog);
    });
    dialog.addEventListener("cancel", (event) => {
      if (dialog.id !== "MenuDrawer" && !dialog.hasAttribute("data-welcome-offer")) return;
      event.preventDefault();
      closeDialog(dialog);
    });
    dialog.addEventListener("close", () => {
      clearOfferMorph(dialog);
      const animation = dialogCloseAnimations.get(dialog);
      dialogCloseAnimations.delete(dialog);
      animation?.cancel();
      dialog.classList.remove("is-closing", "is-minimizing", "offer-morph-entered");
      dialog.style.removeProperty("--offer-backdrop-from");
      dialog.querySelector(".menu-sheet-content")?.style.removeProperty("animation");
      restoreMenuToggle(dialog);
      if (!document.querySelector("dialog[open]")) {
        document.documentElement.classList.remove("dialog-open");
        if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
      }
    });
  }
  function setProductButton(button, label, selection = "") {
    const labelNode = button.querySelector("[data-add-label]");
    if (!labelNode) { button.textContent = label; return; }
    button.dataset.restingLabel = label;
    button.dataset.restingSelection = selection;
    labelNode.textContent = button.dataset.adding === "true" ? "Adding…" : label;
    const detail = button.querySelector("[data-add-selection]");
    if (detail && button.dataset.adding !== "true") {
      detail.textContent = selection;
      detail.hidden = !selection;
    }
  }
  function updateProductVariantDetails(select, option, fromPrice = false) {
    const productPage = select.closest(".product-page");
    const priceContainer = productPage?.querySelector("[data-product-price]");
    if (priceContainer) priceContainer.hidden = false;
    const gallery = select.closest(".product-page")?.querySelector("[data-product-gallery]");
    if (gallery && option.dataset.imageId) selectGalleryImage(gallery, option.dataset.imageId);
    const price = productPage?.querySelector("[data-current-price]");
    if (price) price.textContent = `${fromPrice ? "From " : ""}${option.dataset.price}`;
    const comparePrice = productPage?.querySelector("[data-compare-price]");
    if (comparePrice) {
      comparePrice.hidden = !option.dataset.comparePrice;
      comparePrice.textContent = option.dataset.comparePrice || "";
      comparePrice.setAttribute("aria-label", `Regular price ${option.dataset.comparePrice || ""}`);
    }
    const priceLabel = productPage?.querySelector("[data-price-label]");
    if (priceLabel) priceLabel.textContent = option.dataset.comparePrice ? "Sale price " : "Price ";
    const quantity = select.form?.querySelector('input[name="quantity"]');
    if (quantity) {
      quantity.min = option.dataset.quantityMin || "1";
      quantity.step = option.dataset.quantityIncrement || "1";
      if (option.dataset.quantityMax) quantity.max = option.dataset.quantityMax;
      else quantity.removeAttribute("max");
      if (quantity.validity.rangeUnderflow || quantity.validity.rangeOverflow || quantity.validity.stepMismatch) {
        quantity.value = quantity.min;
      }
      const control = quantity.closest("[data-quantity-control]");
      if (control) syncQuantity(control);
    }
  }
  function updateVariantPicker(picker) {
    const select = picker.querySelector('select[name="id"]');
    const groups = [...picker.querySelectorAll("[data-option-position]")];
    const values = groups.map(group => group.querySelector('input:checked')?.value);
    if (!select || !groups.length) return;
    const variants = [...select.options].filter(option => option.value);
    const match = variants.find(variant => values.every((value, index) => variant.dataset[`option${index + 1}`] === value));
    groups.forEach((group, index) => {
      group.querySelector("[data-selected-option]").textContent = values[index] || (group.dataset.sizeChoice === "true" ? "Choose a size" : "Choose an option");
      group.querySelectorAll('input[type="radio"]').forEach(input => {
        const available = variants.some(variant => variant.dataset.available === "true" && values.every((value, position) => position === index ? variant.dataset[`option${position + 1}`] === input.value : !value || variant.dataset[`option${position + 1}`] === value));
        input.closest(".variant-choice").classList.toggle("is-unavailable", !available);
        input.closest(".variant-choice").querySelector("[data-option-stock]").textContent = available ? "" : " — unavailable with the other selected options";
      });
    });
    const button = picker.closest('form')?.querySelector('[type="submit"]');
    const status = picker.querySelector("[data-variant-status]");
    const incomplete = groups.some((group, index) => !values[index]);
    picker.dataset.selectionIncomplete = String(incomplete);
    if (incomplete) {
      select.disabled = false;
      select.value = "";
      const waitingForSize = groups.some((group, index) => group.dataset.sizeChoice === "true" && !values[index]);
      const previews = variants.filter(variant => values.every((value, index) => !value || variant.dataset[`option${index + 1}`] === value));
      const available = previews.some(variant => variant.dataset.available === "true");
      if (button) {
        if (lockedControls.has(button)) lockedControls.set(button, true);
        button.disabled = true;
        setProductButton(button, !previews.length ? "Unavailable" : !available ? "Sold out" : waitingForSize ? "Choose your size" : "Choose options");
      }
      status.textContent = !previews.length ? "This combination is unavailable. Choose another option." : !available ? "This selection is sold out. Try another color or option." : waitingForSize ? "Select a size to add this item to your bag." : "Choose your options to continue.";
      const preview = [...previews].sort((a, b) => Number(a.dataset.priceAmount) - Number(b.dataset.priceAmount))[0];
      if (preview) updateProductVariantDetails(select, preview, previews.some(option => option.dataset.priceAmount !== preview.dataset.priceAmount));
      const url = new URL(location.href);
      url.searchParams.delete("variant");
      history.replaceState({}, "", url);
      return;
    }
    if (match) {
      select.disabled = false;
      select.value = match.value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      status.textContent = match.dataset.available === "true" ? "" : "This option is sold out. Try another selection.";
    } else {
      select.selectedIndex = -1;
      select.disabled = true;
      const price = picker.closest(".product-page")?.querySelector("[data-product-price]");
      if (price) price.hidden = true;
      if (button) {
        if (lockedControls.has(button)) lockedControls.set(button, true);
        button.disabled = true;
        setProductButton(button, "Unavailable", "Choose another option");
      }
      status.textContent = "This combination is unavailable. Choose another option.";
      const url = new URL(location.href);
      url.searchParams.delete("variant");
      history.replaceState({}, "", url);
    }
  }
  document.addEventListener("change", event => {
    if (!event.target.matches('.variant-choice input[type="radio"]')) return;
    updateVariantPicker(event.target.closest("[data-variant-picker]"));
  });
  function syncQuantity(control) {
    const input = control.querySelector('input[type="number"]');
    if (!input) return;
    const value = input.valueAsNumber;
    const min = input.hasAttribute("min") ? Number(input.min) : -Infinity;
    const max = input.hasAttribute("max") ? Number(input.max) : Infinity;
    const unavailable = input.disabled || input.readOnly;
    control.querySelector('[data-quantity-step="-1"]').disabled = unavailable || (Number.isFinite(value) && value <= min);
    control.querySelector('[data-quantity-step="1"]').disabled = unavailable || (Number.isFinite(value) && value >= max);
  }
  document.addEventListener("click", event => {
    const button = event.target.closest("[data-quantity-step]");
    if (!button || button.disabled) return;
    const control = button.closest("[data-quantity-control]");
    const input = control.querySelector('input[type="number"]');
    if (input.disabled || input.readOnly) return;
    if (!Number.isFinite(input.valueAsNumber)) input.value = input.min || "0";
    else input.stepUp(Number(button.dataset.quantityStep));
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });
  document.addEventListener("input", event => {
    const control = event.target.closest("[data-quantity-control]");
    if (control) syncQuantity(control);
  });
  document.addEventListener("change", event => {
    const control = event.target.closest("[data-quantity-control]");
    if (control) syncQuantity(control);
  });
  function selectGalleryImage(gallery, imageId, { shouldScroll = true, animate = true } = {}) {
    const panels = [...gallery.querySelectorAll("[data-gallery-panel]")];
    const selected = panels.find(panel => panel.dataset.galleryPanel === imageId);
    if (!selected) return;
    const track = gallery.querySelector("[data-gallery-track]");
    if (track) {
      panels.forEach(panel => { panel.hidden = false; });
      if (shouldScroll) {
        const bounds = track.getBoundingClientRect();
        const panelBounds = selected.getBoundingClientRect();
        const delta = getComputedStyle(track).direction === "rtl" ? panelBounds.right - bounds.right : panelBounds.left - bounds.left;
        if (Math.abs(delta) > 1) track.scrollTo({ left: track.scrollLeft + delta, behavior: !animate || !gallery.dataset.initialized || window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? "auto" : "smooth" });
      }
    } else panels.forEach(panel => { panel.hidden = panel !== selected; });
    gallery.dataset.activeImage = imageId;
    gallery.querySelectorAll("[data-gallery-thumbnail]").forEach(thumbnail => {
      if (thumbnail.dataset.galleryThumbnail === imageId) thumbnail.setAttribute("aria-current", "true");
      else thumbnail.removeAttribute("aria-current");
    });
    const status = gallery.querySelector("[data-gallery-status]");
    const index = panels.indexOf(selected);
    const message = `Photo ${index + 1} of ${panels.length}`;
    if (status && status.textContent !== message) status.textContent = message;
    const counter = gallery.querySelector("[data-gallery-counter]");
    if (counter) counter.textContent = `${index + 1} / ${panels.length}`;
    const previous = gallery.querySelector('[data-gallery-step="-1"]');
    const next = gallery.querySelector('[data-gallery-step="1"]');
    if (previous) previous.disabled = index === 0;
    if (next) next.disabled = index === panels.length - 1;
    const thumbnail = gallery.querySelector('[data-gallery-thumbnail][aria-current="true"]');
    const strip = thumbnail?.closest(".gallery-thumbnails");
    if (strip) {
      const itemBounds = thumbnail.getBoundingClientRect();
      const stripBounds = strip.getBoundingClientRect();
      if (itemBounds.left < stripBounds.left) strip.scrollLeft += itemBounds.left - stripBounds.left;
      else if (itemBounds.right > stripBounds.right) strip.scrollLeft += itemBounds.right - stripBounds.right;
    }
  }
  function syncGalleryScroll(gallery) {
    const track = gallery.querySelector("[data-gallery-track]");
    const panels = [...gallery.querySelectorAll("[data-gallery-panel]")];
    if (!track || !panels.length) return;
    const bounds = track.getBoundingClientRect();
    const rtl = getComputedStyle(track).direction === "rtl";
    const distance = panel => Math.abs(rtl ? panel.getBoundingClientRect().right - bounds.right : panel.getBoundingClientRect().left - bounds.left);
    const nearest = panels.reduce((best, panel) => distance(panel) < distance(best) ? panel : best);
    selectGalleryImage(gallery, nearest.dataset.galleryPanel, { shouldScroll: false });
  }
  document.addEventListener("click", event => {
    const step = event.target.closest("[data-gallery-step]");
    if (step && !step.disabled) {
      const gallery = step.closest("[data-product-gallery]");
      const panels = [...gallery.querySelectorAll("[data-gallery-panel]")];
      const index = panels.findIndex(panel => panel.dataset.galleryPanel === gallery.dataset.activeImage);
      const next = panels[Math.max(0, Math.min(panels.length - 1, index + Number(step.dataset.galleryStep)))];
      if (next) selectGalleryImage(gallery, next.dataset.galleryPanel);
      return;
    }
    const thumbnail = event.target.closest("[data-gallery-thumbnail]");
    if (!thumbnail) return;
    event.preventDefault();
    selectGalleryImage(thumbnail.closest("[data-product-gallery]"), thumbnail.dataset.galleryThumbnail);
  });
  document.addEventListener("keydown", event => {
    if (event.target.matches("[data-gallery-track]") && ["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const gallery = event.target.closest("[data-product-gallery]");
      const panels = [...gallery.querySelectorAll("[data-gallery-panel]")];
      if (!panels.length) return;
      let index = panels.findIndex(panel => panel.dataset.galleryPanel === gallery.dataset.activeImage);
      if (event.key === "Home") index = 0;
      else if (event.key === "End") index = panels.length - 1;
      else index = Math.max(0, Math.min(panels.length - 1, index + (event.key === "ArrowRight" ? 1 : -1)));
      selectGalleryImage(gallery, panels[index].dataset.galleryPanel);
      return;
    }
    const thumbnail = event.target.closest("[data-gallery-thumbnail]");
    if (!thumbnail || !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const gallery = thumbnail.closest("[data-product-gallery]");
    const thumbnails = [...gallery.querySelectorAll("[data-gallery-thumbnail]")];
    let index = thumbnails.indexOf(thumbnail);
    if (event.key === "Home") index = 0;
    else if (event.key === "End") index = thumbnails.length - 1;
    else index = (index + (event.key === "ArrowRight" ? 1 : -1) + thumbnails.length) % thumbnails.length;
    thumbnails[index].focus();
    selectGalleryImage(gallery, thumbnails[index].dataset.galleryThumbnail);
  });
  let galleryGesture = null;
  document.addEventListener("pointerdown", event => {
    // A second finger belongs to browser zooming, never to photo navigation.
    if (!event.isPrimary) { galleryGesture = null; return; }
    if (!["touch", "pen"].includes(event.pointerType)) return;
    const surface = event.target.closest("[data-product-gallery] .gallery-main");
    const gallery = surface?.closest("[data-product-gallery]");
    if (!gallery || gallery.querySelector("[data-gallery-track]") || gallery.querySelectorAll("[data-gallery-panel]").length < 2) return;
    galleryGesture = { gallery, surface, pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    surface.setPointerCapture?.(event.pointerId);
  }, { passive: true });
  document.addEventListener("pointerup", event => {
    const gesture = galleryGesture;
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    galleryGesture = null;
    const horizontal = event.clientX - gesture.x;
    const vertical = event.clientY - gesture.y;
    if (Math.abs(horizontal) < 40 || Math.abs(horizontal) <= Math.abs(vertical) * 1.2) return;
    const panels = [...gesture.gallery.querySelectorAll("[data-gallery-panel]")];
    const current = panels.findIndex(panel => !panel.hidden);
    if (current < 0) return;
    const next = (current + (horizontal < 0 ? 1 : -1) + panels.length) % panels.length;
    selectGalleryImage(gesture.gallery, panels[next].dataset.galleryPanel);

  }, { passive: true });
  for (const type of ["pointercancel", "lostpointercapture"]) {
    document.addEventListener(type, event => {
      if (event.pointerId === galleryGesture?.pointerId) galleryGesture = null;
    }, { passive: true });
  }
  class ProductSuggestions extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.loaded) return;
      this.dataset.loaded = "true";
      this.controller = new AbortController();
      const load = async () => {
        try {
          const response = await fetch(this.dataset.url, { signal: this.controller.signal, headers: { Accept: "text/html" } });
          if (!response.ok) return;
          const doc = new DOMParser().parseFromString(await response.text(), "text/html");
          const replacement = [...doc.querySelectorAll("product-suggestions")].find(element => element.dataset.sectionId === this.dataset.sectionId);
          if (replacement?.querySelector(".fashion-product") && !this.contains(document.activeElement)) this.innerHTML = replacement.innerHTML;
        } catch (error) { /* Keep the existing collection cards when recommendations are unavailable. */ }
      };
      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); load(); }
        }, { rootMargin: "400px" });
        this.observer.observe(this);
      } else load();
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  }
  if (!customElements.get("product-suggestions")) customElements.define("product-suggestions", ProductSuggestions);
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.slides = [...this.querySelectorAll("[data-announcement-slide]")];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.paused = this.dataset.autoplay !== "true" || this.reducedMotion.matches;
      this.hovered = false;
      this.focused = false;
      const previous = this.querySelector("[data-announcement-prev]");
      const controls = this.querySelector(".announcement-controls");
      if (previous) previous.hidden = false;
      if (controls) controls.hidden = false;
      this.pauseButton = this.querySelector("[data-announcement-pause]");
      this.querySelector("[data-announcement-prev]")?.addEventListener("click", () => this.show(this.index - 1), { signal });
      this.querySelector("[data-announcement-next]")?.addEventListener("click", () => this.show(this.index + 1), { signal });
      this.pauseButton?.addEventListener("click", () => { this.paused = !this.paused; this.schedule(); }, { signal });
      this.addEventListener("mouseenter", () => { this.hovered = true; this.schedule(); }, { signal });
      this.addEventListener("mouseleave", () => { this.hovered = false; this.schedule(); }, { signal });
      this.addEventListener("focusin", () => { this.focused = true; this.schedule(); }, { signal });
      this.addEventListener("focusout", event => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); } }, { signal });
      document.addEventListener("visibilitychange", () => this.schedule(), { signal });
      this.reducedMotion.addEventListener("change", () => { if (this.reducedMotion.matches) this.paused = true; this.schedule(); }, { signal });
      document.addEventListener("shopify:block:select", event => {
        const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (index >= 0) { this.paused = true; this.show(index); this.schedule(); }
      }, { signal });
      this.show(0);
      this.schedule();
    }
    show(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle("is-active", active);
        slide.inert = !active;
        slide.setAttribute("aria-hidden", String(!active));
      });
    }
    schedule() {
      clearInterval(this.timer);
      if (this.pauseButton) {
        this.pauseButton.textContent = this.paused ? "Play" : "Pause";
        this.pauseButton.setAttribute("aria-label", this.paused ? "Play announcements" : "Pause announcements");
      }
      if (this.paused || this.hovered || this.focused || document.hidden) return;
      const delay = Math.max(3000, Number(this.dataset.interval) || 5000);
      this.timer = setInterval(() => this.show(this.index + 1), delay);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.controller?.abort();
    }
  }
  if (!customElements.get("announcement-rotator")) customElements.define("announcement-rotator", AnnouncementRotator);
  class SaleCountdown extends HTMLElement {
    connectedCallback() {
      this.stop();
      this.hidden = true;
      const deadline = this.dataset.endsAt || "";
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/.test(deadline)) return;
      this.endsAt = Date.parse(deadline);
      if (!Number.isFinite(this.endsAt)) return;
      this.onVisibilityChange = () => this.tick();
      if (this.tick()) {
        this.timer = setInterval(() => this.tick(), 1000);
        document.addEventListener("visibilitychange", this.onVisibilityChange);
      }
    }
    tick() {
      const remaining = Math.ceil((this.endsAt - Date.now()) / 1000);
      if (remaining <= 0) {
        this.hidden = true;
        this.stop();
        return false;
      }
      const units = { days: Math.floor(remaining / 86400), hours: Math.floor(remaining / 3600) % 24, minutes: Math.floor(remaining / 60) % 60, seconds: remaining % 60 };
      for (const [unit, value] of Object.entries(units)) {
        const field = this.querySelector(`[data-countdown-unit="${unit}"]`);
        if (field) field.textContent = String(value).padStart(2, "0");
      }
      this.hidden = false;
      return true;
    }
    stop() {
      clearInterval(this.timer);
      if (this.onVisibilityChange) document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }
    disconnectedCallback() { this.stop(); }
  }
  if (!customElements.get("centrality-sale-countdown")) customElements.define("centrality-sale-countdown", SaleCountdown);
  class ProductSizeGuide extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.unit = this.unit || "cm";
      this.querySelector("[data-size-guide-controls]")?.removeAttribute("hidden");
      this.addEventListener("click", event => {
        const button = event.target.closest("[data-size-unit]");
        if (!button) return;
        this.unit = button.dataset.sizeUnit;
        this.update();
      }, { signal: this.controller.signal });
      this.update();
    }
    update() {
      this.querySelectorAll("[data-size-unit-panel]").forEach(panel => { panel.hidden = panel.dataset.sizeUnitPanel !== this.unit; });
      this.querySelectorAll("[data-size-unit]").forEach(button => { button.setAttribute("aria-pressed", String(button.dataset.sizeUnit === this.unit)); });
    }
    disconnectedCallback() { this.controller?.abort(); }
  }
  if (!customElements.get("centrality-size-guide")) customElements.define("centrality-size-guide", ProductSizeGuide);
  let headerObserver;
  let observedHeader;
  let headerFrame = 0;
  function updateHeaderFloatingState() {
    if (!observedHeader?.isConnected) return;
    const announcementHeight = observedHeader.querySelector(".announcement")?.getBoundingClientRect().height || 0;
    const isFloating = window.scrollY > 0 && observedHeader.getBoundingClientRect().top <= -announcementHeight + 0.5;
    observedHeader.classList.toggle("is-floating", isFloating);
  }
  function scheduleHeaderUpdate() {
    if (headerFrame) return;
    headerFrame = requestAnimationFrame(() => {
      headerFrame = 0;
      updateHeaderFloatingState();
    });
  }
  function initializeOfferSignIn(dialog) {
    const login = dialog.querySelector("[data-offer-sign-in]");
    const message = dialog.querySelector("[data-offer-message]");
    const reward = dialog.querySelector("[data-offer-reward]");
    const codeNode = dialog.querySelector("[data-offer-code]");
    if (!login || !reward || !codeNode || !message) return false;
    const pendingKey = "centrality:welcome:sign-in-pending";
    let pending = false;
    try {
      const started = Number(sessionStorage.getItem(pendingKey));
      const age = Date.now() - started;
      pending = started > 0 && age >= 0 && age < 600000;
    } catch {}
    login.addEventListener("click", () => {
      try { sessionStorage.setItem(pendingKey, String(Date.now())); } catch {}
      // Follow Shopify's storefront-login link in this tab. Shopify supplies the return URL.
    });
    dialog.querySelector("[data-offer-copy]")?.addEventListener("click", async event => {
      const button = event.currentTarget;
      try {
        await navigator.clipboard.writeText(codeNode.textContent.trim());
        button.textContent = "Copied";
        message.textContent = "Code copied. You’re ready to shop.";
      } catch { codeNode.focus(); message.classList.remove("visually-hidden"); message.textContent = "Select the code to copy it."; }
    });
    const signedIn = dialog.dataset.offerSignedIn === "true";
    const code = codeNode.textContent.trim();
    if (!signedIn || !code) return false;
    // The server-rendered customer and code authorize the reward; the session marker only opens it.
    try { sessionStorage.removeItem(pendingKey); sessionStorage.removeItem("centrality:welcome:declined"); } catch {}
    dialog.querySelector("[data-offer-login]").hidden = true;
    reward.hidden = false;
    dialog.querySelector(".sign-in-offer-decline").hidden = true;
    message.classList.add("visually-hidden");
    dialog.querySelector("#SignInOfferTitle").textContent = "You’re in.";
    dialog.querySelector(".sign-in-offer-value").textContent = "Your welcome code is ready.";
    dialog.querySelector(".sign-in-offer-value").hidden = false;
    dialog.querySelector("#SignInOfferDescription").textContent = "Use it at checkout on your first order.";
    message.textContent = "Signed in. Your welcome code is ready.";
    document.querySelector("[data-offer-launcher]")?.setAttribute("hidden", "");
    return pending;
  }
  function initializeWelcomeOffer() {
    const dialog = document.querySelector("[data-welcome-offer]");
    if (!dialog || dialog.dataset.offerBound || typeof dialog.showModal !== "function") return;
    dialog.dataset.offerBound = "true";
    const preview = new URL(window.location.href).searchParams.get("welcome_offer") === "preview" || window.Shopify?.designMode === true;
    const returnedFromSignIn = initializeOfferSignIn(dialog);
    if (dialog.dataset.offerSignedIn === "true" && !preview && !returnedFromSignIn) return;
    const launcher = document.querySelector("[data-offer-launcher]");
    const declinedKey = "centrality:welcome:declined";
    if (launcher) {
      try { launcher.hidden = sessionStorage.getItem(declinedKey) !== "true"; } catch { launcher.hidden = true; }
      dialog.querySelector("[data-offer-decline]")?.addEventListener("click", () => {
        if (dialog.dataset.offerSignedIn === "true") return;
        launcher.hidden = false;
        try { sessionStorage.setItem(declinedKey, "true"); } catch {}
      });
    }
    let shown = false, ready = false;
    const cleanup = () => {
      document.removeEventListener("close", attempt, true);
      document.removeEventListener("visibilitychange", attempt);
      document.removeEventListener("focusout", afterFocus);
    };
    function attempt() {
      if (!ready || shown) return;
      if (!dialog.isConnected) { cleanup(); return; }
      if (document.hidden || document.querySelector("dialog[open]") || document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) return;
      shown = true;
      cleanup();
      openDialog(dialog);
    }
    function afterFocus() { setTimeout(attempt, 0); }
    document.querySelector("[data-offer-launcher]")?.addEventListener("click", () => {
      shown = true;
      cleanup();
    });
    if (dialog.dataset.offerAutoShow !== "true" && !preview && !returnedFromSignIn) return;
    document.addEventListener("close", attempt, true);
    document.addEventListener("visibilitychange", attempt);
    document.addEventListener("focusout", afterFocus);
    const delay = preview || returnedFromSignIn ? 0 : Math.max(0, Number(dialog.dataset.offerDelay) || 0) * 1000;
    setTimeout(() => { ready = true; attempt(); }, delay);
  }
  function initializeCustomerWelcome() {
    const card = document.querySelector("[data-customer-welcome]");
    if (!card || card.dataset.welcomeBound) return;
    card.dataset.welcomeBound = "true";
    const status = document.querySelector("[data-customer-welcome-status]");
    let ready = false, dismissed = false, visible = false, announced = false, hovered = false;
    let remaining = 12000, started = 0, expiry = 0, frame = 0;
    function pause() {
      clearTimeout(expiry);
      if (started) remaining = Math.max(0, remaining - (performance.now() - started));
      started = 0;
    }
    function resume() {
      pause();
      if (!visible || dismissed || hovered || card.contains(document.activeElement) || document.hidden || document.querySelector("dialog[open]")) return;
      started = performance.now();
      expiry = setTimeout(() => dismiss(), remaining);
    }
    function sync() {
      if (dismissed) return;
      if (!card.isConnected) { cleanup(); return; }
      const blocked = !ready || document.hidden || document.querySelector("dialog[open]");
      if (blocked) {
        pause();
        visible = false;
        cancelAnimationFrame(frame);
        card.classList.remove("is-visible");
        card.hidden = true;
        return;
      }
      if (visible) return;
      visible = true;
      card.hidden = false;
      frame = requestAnimationFrame(() => {
        card.classList.add("is-visible");
        if (!announced && status) status.textContent = card.querySelector("h2").textContent;
        announced = true;
        resume();
      });
    }
    function cleanup() {
      pause();
      clearTimeout(entrance);
      cancelAnimationFrame(frame);
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      document.removeEventListener("close", sync, true);
    }
    function dismiss(restoreFocus = false) {
      if (dismissed) return;
      dismissed = true;
      cleanup();
      card.classList.remove("is-visible");
      if (restoreFocus && card.contains(document.activeElement)) {
        const target = [...document.querySelectorAll(".header-tools .account-link, .mobile-menu")].find(element => element.getClientRects().length) || find("MainContent");
        target?.focus({ preventScroll: true });
      }
      if (status) status.textContent = "";
      const delay = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? 0 : 240;
      setTimeout(() => { card.hidden = true; }, delay);
    }
    const observer = new MutationObserver(sync);
    // Native dialogs take priority; resume the greeting after they close.
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ["open"] });
    const entrance = setTimeout(() => { ready = true; sync(); }, 900);
    document.addEventListener("visibilitychange", sync);
    document.addEventListener("close", sync, true);
    card.addEventListener("pointerenter", () => { hovered = true; pause(); });
    card.addEventListener("pointerleave", () => { hovered = false; resume(); });
    card.addEventListener("focusin", pause);
    card.addEventListener("focusout", () => queueMicrotask(resume));
    card.querySelector("[data-dismiss-customer-welcome]").addEventListener("click", event => dismiss(event.detail === 0));
    card.addEventListener("keydown", event => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      dismiss(true);
    });
  }
  function initializeHeroEntrance() {
    document.querySelectorAll("[data-hero-entrance]").forEach(hero => {
      if (hero.dataset.heroEntranceBound) return;
      hero.dataset.heroEntranceBound = "true";
      hero.addEventListener("focusin", () => hero.classList.add("hero-has-interaction"), { once: true });
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
      const copy = hero.querySelector(".campaign-content > h1:not(.visually-hidden), .campaign-content > p, .campaign-actions");
      // Never restart text that has already appeared, including a slow-script fallback.
      if (!copy || Number(getComputedStyle(copy).opacity) !== 0) return;
      const image = hero.querySelector(".campaign-media > img");
      let settled = false, timer = 0;
      const reveal = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        image?.removeEventListener("load", reveal);
        image?.removeEventListener("error", reveal);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (hero.isConnected && Number(getComputedStyle(copy).opacity) === 0) hero.classList.add("hero-is-ready");
        }));
      };
      // CSS also reveals the copy without JavaScript. Do not hold it for a slow image.
      timer = setTimeout(reveal, 850);
      if (!image || image.complete) reveal();
      else {
        image.addEventListener("load", reveal, { once: true });
        image.addEventListener("error", reveal, { once: true });
      }
    });
  }
  function initialize() {
    document.querySelectorAll("[data-initial-collection]").forEach(track => {
      if (track.dataset.initialCollectionBound) return;
      track.dataset.initialCollectionBound = "true";
      requestAnimationFrame(() => {
        const tile = track.children[Number(track.dataset.initialCollection) - 1];
        if (!tile || track.scrollWidth <= track.clientWidth) return;
        const padding = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
        const left = tile.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - padding;
        track.scrollTo({ left, behavior: "instant" });
      });
    });
    document.querySelectorAll("dialog").forEach(bindDialog);
    initializeWelcomeOffer();
    initializeCustomerWelcome();
    initializeHeroEntrance();
    document.querySelectorAll("[data-variant-picker]").forEach(picker => {
      if (picker.dataset.initialized) return;
      const select = picker.querySelector('select[name="id"]');
      const groups = [...picker.querySelectorAll("[data-option-position]")];
      if (!select || !groups.length) return;
      const variants = [...select.options].filter(option => option.value);
      const query = new URL(location.href).searchParams;
      const linked = variants.find(option => option.value === query.get("variant")) || variants.find(option => option.value === picker.dataset.selectedVariant);
      const initial = linked || variants.find(option => option.value === select.value) || variants.find(option => option.dataset.available === "true") || variants[0];
      if (!initial) return;
      groups.forEach((group, index) => {
        const requestedColor = !linked && group.dataset.colorOption === "true" ? query.get("color") : null;
        const colorMatch = requestedColor && [...group.querySelectorAll('input[type="radio"]')].find(input => input.value.toLowerCase() === requestedColor.toLowerCase());
        group.querySelectorAll('input[type="radio"]').forEach(input => {
          input.checked = group.dataset.sizeChoice === "true" && !linked ? false : input.value === (colorMatch?.value || initial.dataset[`option${index + 1}`]);
        });
      });
      picker.querySelectorAll("[data-swatch-name]").forEach(swatch => {
        const color = swatch.dataset.swatchName.trim();
        if (!window.CSS?.supports("color", color) || /^(transparent|currentcolor|inherit|initial|unset|revert|revert-layer)$/i.test(color) || /var\(|env\(/i.test(color)) return;
        swatch.style.backgroundColor = color;
        swatch.style.backgroundImage = "none";
        swatch.querySelector("img")?.setAttribute("hidden", "");
      });
      picker.dataset.initialized = "true";
      picker.querySelector("[data-variant-fallback]").hidden = true;
      picker.querySelector("[data-variant-groups]").hidden = false;
      updateVariantPicker(picker);
    });
    document.querySelectorAll("[data-quantity-control]").forEach(syncQuantity);
    document.querySelectorAll("[data-product-gallery]").forEach(gallery => {
      if (gallery.dataset.initialized) return;
      const track = gallery.querySelector("[data-gallery-track]");
      if (track) {
        let timer;
        track.addEventListener("scroll", () => {
          clearTimeout(timer);
          timer = setTimeout(() => syncGalleryScroll(gallery), 140);
        }, { passive: true });
        track.addEventListener("scrollend", () => { clearTimeout(timer); syncGalleryScroll(gallery); });
        let width = track.clientWidth;
        const resize = new ResizeObserver(() => {
          if (!gallery.isConnected) { resize.disconnect(); return; }
          if (width === track.clientWidth) return;
          width = track.clientWidth;
          clearTimeout(timer);
          if (gallery.dataset.activeImage) selectGalleryImage(gallery, gallery.dataset.activeImage, { animate: false });
        });
        resize.observe(track);
      }
      const first = gallery.querySelector("[data-gallery-panel]");
      if (first) selectGalleryImage(gallery, gallery.dataset.activeImage || gallery.dataset.initialImage || first.dataset.galleryPanel);
      gallery.dataset.initialized = "true";
    });
    const headerSection = document.querySelector(".sticky-header-section");
    if (headerSection && headerSection !== observedHeader) {
      headerObserver?.disconnect();
      observedHeader = headerSection;
      const announcement = headerSection.querySelector(".announcement");
      const updateOffset = () => {
        headerSection.style.setProperty("--announcement-height", `${announcement?.getBoundingClientRect().height || 0}px`);
        updateHeaderFloatingState();
      };
      updateOffset();
      if (announcement) {
        headerObserver = new ResizeObserver(updateOffset);
        headerObserver.observe(announcement);
      }
    }
  }
  function lockCartControls() {
    document.querySelectorAll('[data-cart-key], [data-product-form] button[type="submit"], #CartDrawer .checkout-button').forEach(control => {
      if (!lockedControls.has(control)) lockedControls.set(control, control.disabled);
      control.disabled = true;
    });
  }
  function setBusy(value) {
    busy = value;
    cartDialog()?.setAttribute("aria-busy", String(value));
    document.querySelectorAll("[data-product-form]").forEach(form => form.setAttribute("aria-busy", String(value)));
    if (value) lockCartControls();
    else {
      lockedControls.forEach((disabled, control) => { control.disabled = disabled; });
      lockedControls.clear();
    }
  }
  function setCount(count) {
    document
      .querySelectorAll("[data-cart-count]")
      .forEach((el) => (el.textContent = count));
    document
      .querySelectorAll("[data-open-cart]")
      .forEach((el) => el.setAttribute("aria-label", `Bag, ${count} items`));
  }
  async function refreshCart() {
    const url = new URL(root(), location.origin);
    url.searchParams.set("section_id", "cart-drawer");
    const response = await fetch(url, {
      cache: "no-store",
      headers: { Accept: "text/html" },
    });
    if (!response.ok)
      throw new Error(
        "Your bag could not be refreshed. Please try again or open the full bag.",
      );
    const doc = new DOMParser().parseFromString(
      await response.text(),
      "text/html",
    );
    const content = doc.getElementById("CartDrawerContent");
    if (!content)
      throw new Error(
        "Your bag could not be refreshed. Please open the full bag.",
      );
    const current = find("CartDrawerContent");
    const count = content.querySelector("[data-rendered-cart-count]")?.dataset.renderedCartCount;
    if (!current || count === undefined) throw new Error("Your bag could not be refreshed. Please open the full bag.");
    current.replaceWith(content);
    setCount(count);
    if (busy) lockCartControls();
  }
  async function readResponse(response) {
    let result;
    try {
      result = await response.json();
    } catch {
      throw new Error(
        "We could not confirm the update. Please check your full bag before trying again.",
      );
    }
    if (!response.ok)
      throw new Error(
        typeof result.description === "string"
          ? result.description
          : "This item could not be updated. Please check availability and try again.",
      );
    return result;
  }
  document.addEventListener("click", async (e) => {
    const opener = e.target.closest("[data-open-dialog]");
    if (opener) {
      const dialog = find(opener.dataset.openDialog);
      if (dialog && typeof dialog.showModal === "function") {
        e.preventDefault();
        initialize();
        if (dialog.id === "MenuDrawer" && dialog.open) closeDialog(dialog);
        else openDialog(dialog, opener.hasAttribute("data-offer-launcher"));
      }
      return;
    }
    const close = e.target.closest("[data-close-dialog]");
    if (close) {
      e.preventDefault();
      closeDialog(close.closest("dialog"));
      return;
    }
    const openCart = e.target.closest("[data-open-cart]");
    if (
      openCart &&
      cartDialog() &&
      typeof cartDialog().showModal === "function"
    ) {
      e.preventDefault();
      initialize();
      openDialog(cartDialog());
      if (busy) return;
      clearError();
      setBusy(true);
      try {
        await refreshCart();
      } catch (error) {
        showError(error.message);
      } finally {
        setBusy(false);
      }
      return;
    }
    const change = e.target.closest("[data-cart-key]");
    if (change) {
      e.preventDefault();
      if (busy) return;
      clearError();
      const key = change.dataset.cartKey,
        quantity = Number(change.dataset.cartQuantity);
      const originalControls = [
        ...find("CartDrawerContent").querySelectorAll("[data-cart-key]"),
      ];
      const focusIndex = originalControls.indexOf(change);
      const lineControlIndex = originalControls.filter(control => control.dataset.cartKey === key).indexOf(change);
      if (!key || !Number.isInteger(quantity) || quantity < 0) return;
      setBusy(true);
      let changed = false;
      try {
        const result = await readResponse(
          await fetch(route("cart/change.js"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ id: key, quantity }),
          }),
        );
        changed = true;
        if (Number.isInteger(result.item_count)) setCount(result.item_count);
        await refreshCart();
        announce("Your bag has been updated.");
        setBusy(false);
        const updatedControls = [
          ...find("CartDrawerContent").querySelectorAll("[data-cart-key]"),
        ];
        const sameLine = updatedControls.filter(control => control.dataset.cartKey === key);
        const same = sameLine[lineControlIndex];
        const nearbyIndex = Math.max(0, Math.min(focusIndex, updatedControls.length - 1));
        const nearby = updatedControls.slice(nearbyIndex).concat(updatedControls.slice(0, nearbyIndex));
        const focusTarget =
          (same && !same.disabled ? same : null) ||
          sameLine.find(control => !control.disabled) ||
          nearby.find(control => !control.disabled) ||
          cartDialog().querySelector("[data-close-dialog]");
        focusTarget?.focus();
      } catch (error) {
        showError(changed ? "Your bag was updated, but its display could not be refreshed. Open the full bag to check your items." : error.message);
      } finally {
        if (busy) setBusy(false);
      }
      return;
    }
    if (!e.target.closest(".nav-group"))
      document
        .querySelectorAll(".desktop-nav details[open]")
        .forEach((d) => (d.open = false));
  });
  document.addEventListener("submit", async (e) => {
    const form = e.target.closest("[data-product-form]");
    if (!form) return;
    const variant = form.querySelector('[data-variant-picker] select[name="id"]');
    if (variant && (variant.disabled || !variant.selectedOptions[0] || variant.selectedOptions[0].dataset.available !== "true")) {
      e.preventDefault();
      const incomplete = variant.closest('[data-variant-picker]').dataset.selectionIncomplete === "true";
      const status = form.querySelector("[data-variant-status]");
      if (status && !incomplete) status.textContent = variant.disabled ? "This combination is unavailable. Choose another option." : "This option is sold out. Try another selection.";
      const missingGroup = [...form.querySelectorAll('[data-option-position]')].find(group => !group.querySelector('input:checked'));
      (missingGroup?.querySelector('.variant-choice:not(.is-unavailable) input') || missingGroup?.querySelector('input') || form.querySelector('.variant-choice input:checked'))?.focus();
      return;
    }
    if (!form.checkValidity()) { e.preventDefault(); form.reportValidity(); return; }
    if (form.querySelector('button[type="submit"]')?.disabled) { e.preventDefault(); return; }
    if (!cartDialog() || typeof cartDialog().showModal !== "function") return;
    e.preventDefault();
    if (busy) return;
    const submitter = e.submitter;
    if (submitter?.disabled) return;
    const addingInsideDrawer = Boolean(form.closest("#CartDrawer"));
    const data = new FormData(form);
    const addingButton = form.querySelector('button[type="submit"]');
    const addingLabel = addingButton?.querySelector("[data-add-label]");
    if (addingLabel) {
      addingButton.dataset.restingLabel = addingLabel.textContent;
      addingButton.dataset.adding = "true";
      addingLabel.textContent = "Adding…";
    }
    clearError();
    setBusy(true);
    let added = false;
    try {
      await readResponse(
        await fetch(route("cart/add.js"), {
          method: "POST",
          headers: { Accept: "application/json" },
          body: data,
        }),
      );
      added = true;
      initialize();
      openDialog(cartDialog());
      await refreshCart();
      if (addingInsideDrawer) {
        const title = find("CartTitle");
        title.tabIndex = -1;
        title.focus({ preventScroll: true });
      }
      announce("Added to your bag.");
    } catch (error) {
      initialize();
      openDialog(cartDialog());
      showError(added ? "Your item was added, but the bag could not be refreshed. Open the full bag to check it before adding again." : error.message);
    } finally {
      setBusy(false);
      if (addingLabel) {
        delete addingButton.dataset.adding;
        setProductButton(addingButton, addingButton.dataset.restingLabel, addingButton.dataset.restingSelection || "");
      }
    }
  });
  document.addEventListener("change", (e) => {
    if (!e.target.matches('[data-variant-picker] select[name="id"]')) return;
    const option = e.target.selectedOptions[0];
    if (!option) return;
    updateProductVariantDetails(e.target, option);
    const button = e.target.form?.querySelector('[type="submit"]');
    if (button) {
      const unavailable = option.dataset.available !== "true";
      if (lockedControls.has(button)) lockedControls.set(button, unavailable);
      button.disabled = busy || unavailable;
      setProductButton(button, unavailable ? "Sold out" : "Add to bag", option.dataset.variantTitle || "");
    }
    const url = new URL(location.href);
    url.searchParams.set("variant", e.target.value);
    history.replaceState({}, "", url);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Tab") {
      const dialog = document.querySelector("dialog[open]");
      if (dialog) {
        const focusable = [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(element => element.getClientRects().length && !element.closest("[inert]"));
        const first = focusable[0], last = focusable.at(-1);
        if (!first) e.preventDefault();
        else if (e.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
      }
    }
    if (e.key === "Escape") {
      const dialog = document.querySelector("dialog[open]");
      if (dialog) {
        e.preventDefault();
        closeDialog(dialog);
      }
      document
        .querySelectorAll(".desktop-nav details[open]")
        .forEach((d) => (d.open = false));
    }
  });
  document.addEventListener("shopify:section:load", initialize);
  window.addEventListener("scroll", scheduleHeaderUpdate, { passive: true });
  window.addEventListener("resize", scheduleHeaderUpdate, { passive: true });
  window.addEventListener("pageshow", scheduleHeaderUpdate);
  initialize();
})();
